/**
 * WhatsApp templates (/admin/communications/whatsapp/templates?tab=templates|messages).
 *
 *  - Templates: every template on the church's WhatsApp account (synced from 360dialog every 30 minutes, or Refresh),
 *    with status chips (Approved, Pending, Rejected with the reason, Paused), filters and a details drawer with a
 *    phone-style preview, its variables and the messages that use it.
 *  - Messages: every CCMS message (any module) with its link: link it to an approved template (the link editor maps
 *    each variable to one of the message's fields) or create a template for it.
 *
 * Reads need Communications.WhatsApp.View; Refresh, linking and creating need Communications.WhatsApp.Manage.
 */

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconRefresh, IconPlus, IconLink, IconSearch, IconTemplate, IconMessages, IconAlertTriangle } from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { Stack, Group, Title, Text, Button, Badge, Paper, Tabs, Select, TextInput, Switch, Drawer, Alert, Divider, SimpleGrid, Tooltip, Chip } from '@/components/ui';
import { FadeIn } from '@/components/animations/transitions';
import { DataTable, type DataTableColumn } from '@/components/advanced';
import { EmptyState } from '@/components/ui/data-display';
import { PageHelpButton, useHelpActiveTab } from '@/components/help';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { PERMISSIONS } from '@/config/permissions';
import type { WhatsAppTemplateLibraryItem, WhatsAppMessageDescriptor } from '@actschurch/ccms-sdk';
import { WhatsAppPhonePreview } from './WhatsAppPhonePreview';
import { TemplateLinkEditor } from './TemplateLinkEditor';
import { statusColor, fillText, LINK_STATE_UI } from './whatsappTemplateRules';
import { useWhatsAppTemplateLibrary, useWhatsAppMessages, useSyncWhatsAppTemplates, whatsAppErrorText } from './useWhatsAppTemplates';

export const WHATSAPP_TEMPLATES_PAGE = 'communications.whatsapp-templates';
const PAGE = WHATSAPP_TEMPLATES_PAGE;
type TabKey = 'templates' | 'messages';

const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'never');

function QualityDot({ score }: { score?: string | null }) {
  const color = { GREEN: 'green', YELLOW: 'yellow', RED: 'red' }[(score ?? '').toUpperCase()] ?? 'gray';
  const label = { GREEN: 'High', YELLOW: 'Medium', RED: 'Low' }[(score ?? '').toUpperCase()] ?? 'Not rated yet';
  return (
    <Tooltip label={`Quality: ${label}`} withArrow>
      <Badge variant="dot" color={color} size="sm">
        {label}
      </Badge>
    </Tooltip>
  );
}

type TemplateRow = WhatsAppTemplateLibraryItem & { id: number };
type MessageRow = WhatsAppMessageDescriptor & { id: number };

export default function WhatsAppTemplatesLibraryPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab: TabKey = params.get('tab') === 'messages' ? 'messages' : 'templates';
  useHelpActiveTab(PAGE, tab);
  const { hasRole, hasPermission } = useEnrichedAuth();
  const canManage = hasRole('Admin') || hasPermission(PERMISSIONS.COMMUNICATIONS.WHATSAPP.MANAGE);

  const library = useWhatsAppTemplateLibrary('communications', false);
  const messages = useWhatsAppMessages('communications');
  const sync = useSyncWhatsAppTemplates();

  // Template filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('all');
  const [category, setCategory] = useState<string | null>(null);
  const [language, setLanguage] = useState<string | null>(null);
  const [linkedOnly, setLinkedOnly] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [selected, setSelected] = useState<WhatsAppTemplateLibraryItem | null>(null);

  // Message filters + link editor
  const [msgSearch, setMsgSearch] = useState('');
  const [msgModule, setMsgModule] = useState<string | null>(null);
  const [linkFor, setLinkFor] = useState<WhatsAppMessageDescriptor | null>(null);
  const [linkPick, setLinkPick] = useState<{ name: string; languageCode: string } | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);

  const templates = library.data?.templates ?? [];
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    templates.filter((t) => !t.isDeleted).forEach((t) => (c[t.status.code] = (c[t.status.code] ?? 0) + 1));
    return c;
  }, [templates]);

  const rows: TemplateRow[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates
      .filter((t) => showDeleted || !t.isDeleted)
      .filter((t) => status === 'all' || t.status.code === status)
      .filter((t) => !category || t.category.code === category)
      .filter((t) => !language || t.languageCode === language)
      .filter((t) => !linkedOnly || t.linkedMessages.length > 0)
      .filter((t) => !q || [t.name, t.bodyText ?? '', ...t.linkedMessages.map((m) => m.title)].some((v) => v.toLowerCase().includes(q)));
  }, [templates, search, status, category, language, linkedOnly, showDeleted]);

  const allMessages = messages.data ?? [];
  const moduleOptions = useMemo(() => [...new Set(allMessages.map((m) => m.moduleName))].sort().map((m) => ({ value: m, label: m })), [allMessages]);
  const messageRows: MessageRow[] = useMemo(() => {
    const q = msgSearch.trim().toLowerCase();
    return allMessages
      .filter((m) => !msgModule || m.moduleName === msgModule)
      .filter((m) => !q || [m.title, m.name, String(m.templateId), m.link?.metaTemplateName ?? ''].some((v) => v.toLowerCase().includes(q)))
      .map((m) => ({ ...m, id: m.templateId }));
  }, [allMessages, msgSearch, msgModule]);

  const onSync = async () => {
    try {
      const r = await sync.mutateAsync();
      if (r.success) toast.success('Templates refreshed', `${r.total} on the account: ${r.added} new, ${r.updated} changed, ${r.removed} gone.`);
      else toast.error('Refresh failed', r.error ?? '360dialog did not answer.');
    } catch (err) {
      toast.error('Refresh failed', whatsAppErrorText(err, '360dialog did not answer.'));
    }
  };

  const openLink = (m: WhatsAppMessageDescriptor | null, pick?: WhatsAppTemplateLibraryItem) => {
    setLinkFor(m);
    setLinkPick(pick ? { name: pick.name, languageCode: pick.languageCode } : null);
    setLinkOpen(true);
  };

  const templateColumns: DataTableColumn<TemplateRow>[] = [
    {
      key: 'name',
      label: 'Template',
      sortable: true,
      render: (_v, r) => (
        <Stack gap={0}>
          <Text size="sm" ff="monospace" fw={500}>
            {r.name}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {r.bodyText}
          </Text>
        </Stack>
      ),
    },
    { key: 'languageCode', label: 'Language', sortable: true, width: 90, render: (v) => <Text size="sm">{v}</Text> },
    {
      key: 'category',
      label: 'Category',
      width: 130,
      render: (_v, r) => (
        <Badge variant="light" color={r.category.color ?? 'gray'} size="sm">
          {r.category.name}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 150,
      render: (_v, r) => {
        const chip = (
          <Badge variant={r.status.code === 'REJECTED' ? 'filled' : 'light'} color={statusColor(r.status.code, r.status.color)} size="sm">
            {r.isDeleted ? 'Deleted' : r.status.name}
          </Badge>
        );
        return r.rejectedReason ? (
          <Tooltip label={`Reason: ${r.rejectedReason}`} withArrow multiline w={280}>
            <span>{chip}</span>
          </Tooltip>
        ) : (
          chip
        );
      },
    },
    { key: 'qualityScore', label: 'Quality', width: 120, render: (_v, r) => <QualityDot score={r.qualityScore} /> },
    {
      key: 'linkedMessages',
      label: 'Used by',
      width: 160,
      render: (_v, r) =>
        r.linkedMessages.length === 0 ? (
          <Text size="xs" c="dimmed">
            No message yet
          </Text>
        ) : (
          <Text size="xs" lineClamp={2}>
            {r.linkedMessages.map((m) => m.title).join(', ')}
          </Text>
        ),
    },
  ];

  const messageColumns: DataTableColumn<MessageRow>[] = [
    {
      key: 'title',
      label: 'Message',
      sortable: true,
      render: (_v, r) => (
        <Stack gap={0}>
          <Text size="sm" fw={500}>
            {r.title}
          </Text>
          <Text size="xs" c="dimmed">
            {r.moduleName} · {r.templateId}
            {r.audience ? ` · ${r.audience}` : ''}
          </Text>
        </Stack>
      ),
    },
    {
      key: 'channels',
      label: 'CCMS texts',
      width: 170,
      render: (_v, r) => (
        <Group gap={4}>
          {r.channels.email && <Badge size="xs" variant="light">Email</Badge>}
          {r.channels.sms && <Badge size="xs" variant="light" color="grape">SMS</Badge>}
          {r.channels.whatsApp && <Badge size="xs" variant="light" color="teal">WhatsApp</Badge>}
          {r.channels.push && <Badge size="xs" variant="light" color="blue">Push</Badge>}
        </Group>
      ),
    },
    {
      key: 'link',
      label: 'WhatsApp template',
      width: 240,
      render: (_v, r) => {
        if (r.isSecurity) return <Text size="xs" c="dimmed">Never by WhatsApp (security)</Text>;
        if (!r.whatsAppAllowed) return <Text size="xs" c="dimmed">Not sent by WhatsApp</Text>;
        const s = LINK_STATE_UI[r.link?.state ?? 'none'] ?? LINK_STATE_UI.none;
        return (
          <Stack gap={2}>
            <Badge size="sm" variant="light" color={s.color}>
              {s.label}
            </Badge>
            {r.link && (
              <Text size="xs" ff="monospace" c="dimmed" truncate>
                {r.link.metaTemplateName}
              </Text>
            )}
          </Stack>
        );
      },
    },
  ];

  const detailParts = selected
    ? {
        headerText: selected.headerText ?? null,
        bodyText: fillText(selected.bodyText ?? '', (v) => selected.variables.find((x) => x.component === 'body' && (v.name ? x.name === v.name : x.index === v.number))?.example ?? null),
        footerText: selected.footerText ?? null,
        buttons: selected.buttons.map((b) => ({ type: b.type, text: b.text, url: b.url })),
      }
    : null;

  return (
    <LayoutContainer py="xl">
      <FadeIn delay={80}>
        <Stack gap="lg">
          <Group justify="space-between" wrap="wrap" align="flex-start" gap="sm">
            <div style={{ minWidth: 0, flex: 1 }}>
              <Group gap="sm">
                <IconTemplate size={28} />
                <Title order={2}>WhatsApp templates</Title>
              </Group>
              <Text size="sm" c="dimmed">
                Messages Meta approved for the church's WhatsApp number, and which CCMS message each one sends. Synced from 360dialog
                every 30 minutes; last refreshed {when(library.data?.lastSync?.syncedAt)}.
              </Text>
            </div>
            <Group gap="sm" data-help-id={`${PAGE}.actions`} wrap="wrap">
              {canManage && (
                <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={onSync} loading={sync.isPending} data-help-id={`${PAGE}.refresh`}>
                  Refresh
                </Button>
              )}
              {canManage && (
                <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/admin/communications/whatsapp/templates/new')} data-help-id={`${PAGE}.new`}>
                  New template
                </Button>
              )}
              <PageHelpButton pageKey={PAGE} />
            </Group>
          </Group>

          {library.error && <Alert color="red">{whatsAppErrorText(library.error, "Couldn't load the templates.")}</Alert>}
          {library.data?.lastSync && !library.data.lastSync.success && (
            <Alert color="orange" icon={<IconAlertTriangle size={16} />}>
              The last refresh failed: {library.data.lastSync.error}. The list shows what was synced before.
            </Alert>
          )}

          <Tabs value={tab} onChange={(v) => setParams(v === 'messages' ? { tab: 'messages' } : {}, { replace: true })}>
            <Tabs.List data-help-id={`${PAGE}.tabs`}>
              <Tabs.Tab value="templates" leftSection={<IconTemplate size={16} />}>
                Templates
              </Tabs.Tab>
              <Tabs.Tab value="messages" leftSection={<IconMessages size={16} />}>
                CCMS messages
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="templates" pt="md">
              <Stack gap="md">
                <Group gap="xs" data-help-id={`${PAGE}.status-chips`} wrap="wrap">
                  <Chip checked={status === 'all'} onChange={() => setStatus('all')} size="sm" variant="light">
                    All ({templates.filter((t) => !t.isDeleted).length})
                  </Chip>
                  {(library.data?.statuses ?? [])
                    .filter((s) => ['APPROVED', 'PENDING', 'REJECTED', 'PAUSED'].includes(s.code) || (counts[s.code] ?? 0) > 0)
                    .map((s) => (
                      <Chip key={s.id} checked={status === s.code} onChange={() => setStatus(status === s.code ? 'all' : s.code)} size="sm" variant="light" color={statusColor(s.code, s.color)}>
                        {s.name} ({counts[s.code] ?? 0})
                      </Chip>
                    ))}
                </Group>

                <Paper withBorder p="md" radius="md" data-help-id={`${PAGE}.filters`}>
                  <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="sm">
                    <TextInput placeholder="Search name, wording or message" leftSection={<IconSearch size={16} />} value={search} onChange={(e) => setSearch(e.currentTarget.value)} />
                    <Select
                      placeholder="Any category"
                      clearable
                      data={(library.data?.categories ?? []).filter((c) => c.code !== 'UNKNOWN').map((c) => ({ value: c.code, label: c.name }))}
                      value={category}
                      onChange={setCategory}
                    />
                    <Select placeholder="Any language" clearable data={[...new Set(templates.map((t) => t.languageCode))].sort()} value={language} onChange={setLanguage} />
                    <Group gap="md">
                      <Switch label="Linked only" checked={linkedOnly} onChange={(e) => setLinkedOnly(e.currentTarget.checked)} />
                      <Switch label="Deleted" checked={showDeleted} onChange={(e) => setShowDeleted(e.currentTarget.checked)} />
                    </Group>
                  </SimpleGrid>
                </Paper>

                <Paper withBorder radius="md" p={0} data-help-id={`${PAGE}.table`}>
                  {!library.isLoading && templates.length === 0 ? (
                    <EmptyState
                      title="No templates yet"
                      description="Press Refresh to read the templates from 360dialog, or create the first one with New template."
                    />
                  ) : (
                    <DataTable
                      columns={templateColumns}
                      data={rows}
                      loading={library.isLoading}
                      emptyMessage="No templates match the filters"
                      enableInternalSearch={false}
                      onRowClick={(r) => setSelected(r)}
                      highlightOnHover
                    />
                  )}
                </Paper>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="messages" pt="md">
              <Stack gap="md">
                <Alert color="blue">
                  A message goes by WhatsApp as the approved template linked to it. Without one it goes as plain text only to people who
                  messaged the church in the last 24 hours, otherwise by SMS when the module's SMS is on.
                </Alert>
                <Paper withBorder p="md" radius="md" data-help-id={`${PAGE}.message-filters`}>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                    <TextInput placeholder="Search messages" leftSection={<IconSearch size={16} />} value={msgSearch} onChange={(e) => setMsgSearch(e.currentTarget.value)} />
                    <Select placeholder="Every module" clearable searchable data={moduleOptions} value={msgModule} onChange={setMsgModule} />
                  </SimpleGrid>
                </Paper>
                <Paper withBorder radius="md" p={0} data-help-id={`${PAGE}.messages`}>
                  <DataTable
                    columns={messageColumns}
                    data={messageRows}
                    loading={messages.isLoading}
                    emptyMessage="No messages match"
                    enableInternalSearch={false}
                    highlightOnHover
                    actions={
                      canManage
                        ? (r) =>
                            r.isSecurity || !r.whatsAppAllowed ? null : (
                              <Group gap={4} wrap="nowrap">
                                <Button size="compact-sm" variant="subtle" leftSection={<IconLink size={14} />} onClick={(e: React.MouseEvent) => { e.stopPropagation(); openLink(r); }}>
                                  {r.link ? 'Change' : 'Link'}
                                </Button>
                                <Button size="compact-sm" variant="subtle" leftSection={<IconPlus size={14} />} onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigate(`/admin/communications/whatsapp/templates/new?message=${r.templateId}`); }}>
                                  Create
                                </Button>
                              </Group>
                            )
                        : undefined
                    }
                  />
                </Paper>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </FadeIn>

      <Drawer opened={!!selected} onClose={() => setSelected(null)} position="right" size="lg" title={selected?.name}>
        {selected && detailParts && (
          <Stack gap="md">
            <Group gap="xs">
              <Badge variant="light" color={statusColor(selected.status.code, selected.status.color)}>
                {selected.isDeleted ? 'Deleted' : selected.status.name}
              </Badge>
              <Badge variant="light" color={selected.category.color ?? 'gray'}>
                {selected.category.name}
              </Badge>
              <Badge variant="light" color="gray">
                {selected.languageCode}
              </Badge>
              <QualityDot score={selected.qualityScore} />
            </Group>
            {selected.status.description && (
              <Text size="sm" c="dimmed">
                {selected.status.description}
              </Text>
            )}
            {selected.rejectedReason && (
              <Alert color="red" title="Why Meta refused it">
                {selected.rejectedReason}
              </Alert>
            )}
            <WhatsAppPhonePreview parts={detailParts} />
            <Text size="xs" c="dimmed" ta="center">
              Shown with Meta's sample values.
            </Text>

            <Divider label="Variables" labelPosition="left" />
            {selected.variables.length === 0 ? (
              <Text size="sm" c="dimmed">
                None: it is sent exactly as approved.
              </Text>
            ) : (
              <Stack gap={4}>
                {selected.variables.map((v) => (
                  <Group key={`${v.component}${v.index}${v.buttonIndex ?? ''}`} justify="space-between" gap="xs">
                    <Text size="sm" ff="monospace">
                      {v.label}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {v.example ? `Sample: ${v.example}` : 'No sample'}
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}

            <Divider label="Used by" labelPosition="left" />
            {selected.linkedMessages.length === 0 ? (
              <Text size="sm" c="dimmed">
                No CCMS message is linked to it yet.
              </Text>
            ) : (
              <Stack gap={4}>
                {selected.linkedMessages.map((m) => (
                  <Group key={m.templateId} justify="space-between">
                    <Text size="sm">
                      {m.title} <Text span size="xs" c="dimmed">({m.module})</Text>
                    </Text>
                    {canManage && (
                      <Button size="compact-xs" variant="subtle" onClick={() => openLink(allMessages.find((x) => x.templateId === m.templateId) ?? null)}>
                        Edit link
                      </Button>
                    )}
                  </Group>
                ))}
              </Stack>
            )}
            <Text size="xs" c="dimmed">
              Last synced {when(selected.lastSyncedAt)}.
            </Text>
            {canManage && selected.status.canSend && !selected.isDeleted && (
              <Button leftSection={<IconLink size={16} />} onClick={() => openLink(null, selected)}>
                Link to a message
              </Button>
            )}
          </Stack>
        )}
      </Drawer>

      <TemplateLinkEditor
        opened={linkOpen}
        onClose={() => setLinkOpen(false)}
        message={linkFor}
        messages={linkFor ? undefined : allMessages}
        initialTemplate={linkPick}
        scope="communications"
      />
    </LayoutContainer>
  );
}
